import { uniq, isEmpty } from 'lodash';
import {
  getFirstSelectedSubscription,
  getSelectedSubscriptionList,
  selectSubscriptionSystem,
} from '../../../selectors/tools';
import { getChildrenOfCurrentDecisionNode, getDecisionNodeAtPath } from '../../../extractFromState';
import {
  cancelSubscriptionsFromDecisionTree,
  lastOrderOfSubscriptionIsFulfilled,
  setSelectedSubscriptionForConfirmationPage,
} from '../CommonSubscriptionActions';
import { decisionTreeCaseNotHandled, setLoading, setLoadingUnique } from '../../index';
import {
  UnexpectedDecisionTreeEdge,
  UnhandledDecisionTreeCase,
  UnhandledDecisionTreeCaseMultipleOrders,
} from '../../../util';
import {
  trackDecisionTreeSubscriptionCancelled,
  trackDecisionTreeSubscriptionSelected,
} from '../../../analytics/searchEventAnalytics';
import { CANCEL_SUBSCRIPTION_ERROR_MESSAGE, CHOOSE_SUB_TOKENS } from '../../../constants/decisionTree';
import { addEdgeToDecisionTreePath } from '../../searchActions';
import { showErrorMessage } from '../../../util/errorPopupUtil';
import { createLastMileRecord } from '../LastMileActions';
import { setShowConfirmationPage } from '../OrdersSearchActions';
import { handleError } from '../../../services/ErrorService/ErrorService';

export const processCommonSubscriptionFlow = () => async (dispatch, getState) => {
  try {
    const state = getState();
    const selectedSubscriptionList = getSelectedSubscriptionList(state);
    const subscriptionSystem = selectSubscriptionSystem(state);
    const decisionNodeChildren = getChildrenOfCurrentDecisionNode(state);
    const decisionNode = getDecisionNodeAtPath(state);

    // clear previous selected subscription record
    dispatch(setSelectedSubscriptionForConfirmationPage(null));
    dispatch(setLoading(true, 'searchPage'));

    // only handling subscriptions in the same state for now
    const allStates = uniq(selectedSubscriptionList.map((subscription) => subscription['status']));
    if (allStates.length > 1) {
      throw new UnhandledDecisionTreeCase(
        'Selected subscriptions which are in different states (e.g. CANCELLED or ACTIVE)'
      );
    }

    const status = allStates[0];
    let currentPath = [];

    trackDecisionTreeSubscriptionSelected();

    for (let i = 0; i < CHOOSE_SUB_TOKENS.length; i++) {
      const options = CHOOSE_SUB_TOKENS[i];
      // eslint-disable-next-line default-case
      switch (i) {
        case 0:
          currentPath.push(options.PAY_AS_YOU_GO);
          break;
        case 1:
          currentPath.push(options[status]);
          break;
        case 2:
          let isFulfilled;
          if (decisionNode?.onlyCancelSubscription) {
            isFulfilled = true;
          } else {
            isFulfilled = await dispatch(lastOrderOfSubscriptionIsFulfilled(subscriptionSystem));
          }
          currentPath.push(isFulfilled ? options.CURRENT_ORDER_FULFILLED : options.CURRENT_ORDER_UNFULFILLED);
          break;
      }
      const strPath = currentPath.join('__');
      if (strPath in decisionNodeChildren) {
        dispatch(addEdgeToDecisionTreePath(strPath));

        return;
      }
    }
    // did not find any match which should not happen
    const strPath = currentPath.join('__');
    throw new UnexpectedDecisionTreeEdge(strPath);
  } catch (err) {
    if (err instanceof UnhandledDecisionTreeCase) {
      dispatch(decisionTreeCaseNotHandled(err.message, err.name));
    } else if (err instanceof UnhandledDecisionTreeCaseMultipleOrders) {
      dispatch(decisionTreeCaseNotHandled(err.message, err.name));
    } else {
      showErrorMessage();
    }
  } finally {
    dispatch(setLoading(false, 'searchPage'));
  }
};

export const processCancelSubscription =
  ({ cancelNonShippedOrders = false, cancellationReason = null, cancellationNote = null, checkNextCharge }) =>
  async (dispatch, getState) => {
    const state = getState();
    const selectedSubscription = getFirstSelectedSubscription(state);

    if (isEmpty(selectedSubscription)) {
      throw new Error('No selected subscription');
    }

    dispatch(setLoadingUnique(true, 'CopyTextContainer'));

    if (checkNextCharge && selectedSubscription && selectedSubscription.next_charge_scheduled_at) {
      dispatch(setSelectedSubscriptionForConfirmationPage(selectedSubscription));
    }

    trackDecisionTreeSubscriptionCancelled();
    try {
      await dispatch(
        cancelSubscriptionsFromDecisionTree({
          cancelNonShippedOrders,
          cancellationReason,
          cancellationNote,
        })
      );
      dispatch(createLastMileRecord());
      dispatch(setShowConfirmationPage());
    } catch (error) {
      handleError({
        error,
        customMessage: CANCEL_SUBSCRIPTION_ERROR_MESSAGE,
        showPopup: true,
      });
    } finally {
      dispatch(setLoadingUnique(false, 'CopyTextContainer'));
    }
  };
